import { CHANGE, UNREGISTER_FIELD } from 'redux-form/lib/actionTypes';
import { formName } from '../Constants/constants';
import {
  SET_ADDITIONAL_COVERAGES_PRESETS,
  DEFAULT_ADDITIONAL_COVERAGES_FORM,
  DELETE_DATE,
  SET_VENUE_CODE,
} from '../Actions/actions';
import initialFormValues from '../Constants/initialFormValues';
import { BY_VENUE_CODE } from '../Constants/venueSearchTypes';
import { isAfter, addDays, isSameDay } from 'date-fns';
import { dateHelpers } from '@jauntin/utilities';

const { pickedToBlockedDays, sortDays, filterToUniqueDays } = dateHelpers;

// Default Additional Coverages state if no venue specific presets
const defaultAdditionalCoveragesForm = (state) => {
  const updated = { ...state };
  const initial = initialFormValues();
  updated.values.ACPersonalProperty = initial.ACPersonalProperty;
  updated.values.ACHostLiquor = initial.ACHostLiquor;
  updated.values.ACPersonalAndAdvertisingLimit =
    initial.ACPersonalAndAdvertisingLimit;
  updated.values.ACProductsAndCompletedOperations =
    initial.ACProductsAndCompletedOperations;
  updated.values.ACSellingLiquor = initial.ACSellingLiquor;
  updated.values.ACTerrorism = initial.ACTerrorism;
  updated.values.ACDamageToRentedProperty = initial.ACDamageToRentedProperty;
  return updated;
};

// Clear previous Additional Coverage form for venue specific presets - temporarily the same as defaults above but this may change in the future
const clearAdditionalCoveragesForm = (state) => {
  const updated = { ...state };
  const initial = initialFormValues();
  updated.values.ACPersonalProperty = initial.ACPersonalProperty;
  updated.values.ACHostLiquor = initial.ACHostLiquor;
  updated.values.ACPersonalAndAdvertisingLimit =
    initial.ACPersonalAndAdvertisingLimit;
  updated.values.ACProductsAndCompletedOperations =
    initial.ACProductsAndCompletedOperations;
  updated.values.ACSellingLiquor = initial.ACSellingLiquor;
  updated.values.ACTerrorism = initial.ACTerrorism;
  updated.values.ACDamageToRentedProperty = initial.ACDamageToRentedProperty;
  return updated;
};

const updateEventDatesFromOtherFields = ({
  daysOfWeekField,
  eventDateRangeField,
}) => {
  let { startDate, endDate } = { ...eventDateRangeField };
  if (!startDate && !endDate) {
    return [];
  }

  const ignoredDays =
    (daysOfWeekField.length && pickedToBlockedDays(daysOfWeekField)) || [];

  if (!startDate) {
    startDate = endDate;
  } else if (!endDate) {
    endDate = startDate;
  }
  const days = [];
  let currentDate = new Date(startDate);
  while (!isAfter(currentDate, new Date(endDate))) {
    if (!ignoredDays.includes(currentDate.getDay())) {
      days.push(currentDate);
    }
    currentDate = addDays(currentDate, 1);
  }
  return sortDays(filterToUniqueDays(days)).map((date) => date.toISOString());
};

const handleEventDateRangeChange = (state, value) => {
  const updated = { ...state };
  updated.values.eventDates = updateEventDatesFromOtherFields({
    daysOfWeekField: state.values.daysOfWeekField,
    eventDateRangeField: value,
  });
  // Reset all Provider details fields *except count
  updated.values.performersList = initialFormValues().performersList;
  updated.values.performersFrequency = initialFormValues().performersFrequency;
  updated.values.goodsVendorsList = initialFormValues().goodsVendorsList;
  updated.values.goodsVendorsFrequency =
    initialFormValues().goodsVendorsFrequency;
  updated.values.foodVendorsList = initialFormValues().foodVendorsList;
  updated.values.foodVendorsFrequency =
    initialFormValues().foodVendorsFrequency;
  updated.values.exhibitorsList = initialFormValues().exhibitorsList;
  updated.values.exhibitorsFrequency = initialFormValues().exhibitorsFrequency;
  return updated;
};
const handleDaysOfWeekFieldChange = (state, action) => {
  const updated = { ...state };
  const element = parseInt(action.meta.field.replace(/[^0-9]/g, ''), 10);
  const value = [...updated.values.daysOfWeekField];
  value[element] = action.payload;
  updated.values.eventDates = updateEventDatesFromOtherFields({
    daysOfWeekField: value,
    eventDateRangeField: updated.values.eventDateRange,
  });
  return updated;
};

const handleEventFrequencyFieldChange = (state /* , value */) => {
  const updated = { ...state };
  // In any situation, whenever eventFrequencyField changes, both date picker
  // values should reset
  updated.values.eventDateRange = initialFormValues().eventDateRange;
  updated.values.eventDates = initialFormValues().eventDates;
  // Also daysOfWeekField will change
  updated.values.daysOfWeekField = initialFormValues().daysOfWeekField;
  // Reset all Provider details fields *except count
  updated.values.performersList = initialFormValues().performersList;
  updated.values.performersFrequency = initialFormValues().performersFrequency;
  updated.values.goodsVendorsList = initialFormValues().goodsVendorsList;
  updated.values.goodsVendorsFrequency =
    initialFormValues().goodsVendorsFrequency;
  updated.values.foodVendorsList = initialFormValues().foodVendorsList;
  updated.values.foodVendorsFrequency =
    initialFormValues().foodVendorsFrequency;
  updated.values.exhibitorsList = initialFormValues().exhibitorsList;
  updated.values.exhibitorsFrequency = initialFormValues().exhibitorsFrequency;
  return updated;
};
const handleEventDatesFieldChange = (state, value) => ({
  ...state,
  values: {
    ...state.values,
    eventDates: sortDays(
      filterToUniqueDays(value.map((date) => new Date(date))),
    ).map((date) => date.toISOString()),
  },
});

const handleProviderFrequencyChange = (state, field) => {
  const updated = { ...state };
  if (field === 'performersFrequency')
    updated.values.performersList = initialFormValues().performersList;
  if (field === 'goodsVendorsFrequency')
    updated.values.goodsVendorsList = initialFormValues().goodsVendorsList;
  if (field === 'foodVendorsFrequency')
    updated.values.foodVendorsList = initialFormValues().foodVendorsList;
  if (field === 'exhibitorsFrequency')
    updated.values.exhibitorsList = initialFormValues().exhibitorsList;
  return updated;
};

const handleProviderCountChange = (state, field) => {
  const updated = { ...state };
  if (field === 'eventPerformersCount') {
    updated.values.performersList = initialFormValues().performersList;
    updated.values.performersFrequency =
      initialFormValues().performersFrequency;
  }
  if (field === 'eventGoods') {
    updated.values.goodsVendorsList = initialFormValues().goodsVendorsList;
    updated.values.goodsVendorsFrequency =
      initialFormValues().goodsVendorsFrequency;
  }
  if (field === 'eventFood') {
    updated.values.foodVendorsList = initialFormValues().foodVendorsList;
    updated.values.foodVendorsFrequency =
      initialFormValues().foodVendorsFrequency;
  }
  if (field === 'eventExhibition') {
    updated.values.exhibitorsList = initialFormValues().exhibitorsList;
    updated.values.exhibitorsFrequency =
      initialFormValues().exhibitorsFrequency;
  }
  return updated;
};

const handleFormChange = (state, action) => {
  const updated = { ...state };

  // Reset all 'venue' parameters if any changes are made
  if (action.meta.field === 'venue') {
    updated.values.federalEntity = '';
    updated.values.kentuckyStateEntity = '';
  }
  // Reset eventPerformers, eventGoods, eventFood, and eventExhibition to zero if user selects 'No' re non-guests attending without own insurance. Otherwise, these values are maintained in the quote API call, resulting in an inaccurate quote.
  if (action.meta.field === 'eventPerformers' && action.payload === 'no') {
    updated.values.eventPerformersCount = '0';
    updated.values.eventGoods = '0';
    updated.values.eventFood = '0';
    updated.values.eventExhibition = '0';
  }

  if (action.meta.field === 'eventDateRange')
    return handleEventDateRangeChange(updated, action.payload);
  if (action.meta.field.match('daysOfWeekField'))
    return handleDaysOfWeekFieldChange(updated, action);
  if (action.meta.field === 'eventFrequencyField')
    return handleEventFrequencyFieldChange(updated, action.payload);
  if (action.meta.field === 'eventDates')
    return handleEventDatesFieldChange(updated, action.payload);
  if (
    (action.meta.field === 'performersFrequency' ||
      action.meta.field === 'goodsVendorsFrequency' ||
      action.meta.field === 'foodVendorsFrequency' ||
      action.meta.field === 'exhibitorsFrequency') &&
    action.payload === 'everyday'
  )
    return handleProviderFrequencyChange(updated, action.meta.field);
  if (
    (action.meta.field === 'eventPerformersCount' ||
      action.meta.field === 'eventGoods' ||
      action.meta.field === 'eventFood' ||
      action.meta.field === 'eventExhibition') &&
    (action.payload === '0' || action.payload === '7')
  )
    return handleProviderCountChange(updated, action.meta.field);

  return updated;
};

const deleteDate = (state, date) => {
  const updated = { ...state };
  updated.values.eventDates = updated.values.eventDates.filter(
    (a) => !isSameDay(new Date(a), new Date(date)),
  );
  // Whenever a date is deleted, we must force the UI to show the Custom date picker
  // To make it possible to show and select specific days
  updated.values.eventFrequencyField = 'custom';
  return updated;
};

// Any sync errors associated with an unregistered field will be removed
const handleUnregisterField = (state, { name }) => {
  const updated = { ...state };
  delete (updated.syncErrors || {})[name];
  return updated;
};

const setVenueCode = (state, params) => {
  const updated = { ...state };
  const venueSearchTypeRadio = BY_VENUE_CODE;
  updated.values.facilityCode = params.facilityCode;
  updated.values.venueCode = params.venueCode;
  updated.values.venueSearchTypeRadio = venueSearchTypeRadio;
  return updated;
};

const formReducers = {
  [formName]: (state, action) => {
    const updated = { ...state };

    if (action.type === CHANGE && action.meta.form === formName) {
      return { ...handleFormChange(state, action) };
    }

    // Sets the requisite fields according to venue (where applicable). Fields are hidden or shown, and their checkboxes are either mandatory (and checked) or not mandatory (and optional)
    if (action.type === SET_ADDITIONAL_COVERAGES_PRESETS) {
      // Reset Additional Coverages to default values
      clearAdditionalCoveragesForm(updated);
      const coverageTypes = action.payload.venue.additionalCoverage;

      const coverageTypeMap = {
        'host-liquor': 'ACHostLiquor',
        'personal-and-advertising-limit': 'ACPersonalAndAdvertisingLimit',
        'products-and-completed-operations': 'ACProductsAndCompletedOperations',
        'liquor-liability': 'ACSellingLiquor',
        terrorism: 'ACTerrorism',
        'damage-to-rented-property': 'ACDamageToRentedProperty',
        'personal-property': 'ACPersonalProperty',
      };

      Object.keys(coverageTypes).map((key) => {
        const type = coverageTypeMap[coverageTypes[key].type];
        updated.values[type] = coverageTypes[key].mandatory.toString() === '1';
        return updated;
      });
    }

    if (action.type === DEFAULT_ADDITIONAL_COVERAGES_FORM) {
      return { ...defaultAdditionalCoveragesForm(updated) };
    }

    if (action.type === DELETE_DATE) {
      return { ...deleteDate(updated, action.payload.date) };
    }

    if (action.type === SET_VENUE_CODE) {
      return { ...setVenueCode(updated, action.payload) };
    }

    if (action.type === UNREGISTER_FIELD) {
      return { ...handleUnregisterField(updated, action.payload) };
    }

    return updated;
  },
};

export default formReducers;
